
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuBadge,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
} from "@/components/ui/sidebar";
import { MenuCategory } from "@/data/dashboardData";
import { getIcon } from "@/utils/iconMapping";
import { LogOut, Home } from "lucide-react";
import logo from "@/assets/logo.png";

interface AdminSidebarProps {
  menuItems: MenuCategory[];
}

// Map sidebar paths to database categories
const pathToCategoryMap: Record<string, string> = {
  "/admin/events": "all",
  "/admin/exhibitions": "exhibitions",
  "/admin/festivals": "festivals",
  "/admin/seasons": "seasons",
};

const AdminSidebar: React.FC<AdminSidebarProps> = ({ menuItems }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const isRTL = i18n.language === "ar";

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin-login");
  };
  
  const { data: categoryCounts = {} } = useQuery({
    queryKey: ["eventCategoryCounts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scraped_events")
        .select("category")
        .eq("is_published", true);
      
      if (error) throw error;
      
      const counts: Record<string, number> = { all: data.length };
      data.forEach((event) => {
        if (event.category) {
          counts[event.category] = (counts[event.category] || 0) + 1;
        }
      });
      
      return counts;
    },
  });

  const getBadge = (path: string, originalBadge?: string) => {
    const category = pathToCategoryMap[path];
    if (category && categoryCounts[category] !== undefined) {
      return categoryCounts[category].toString();
    }
    return originalBadge;
  };
  
    const sidebarBg = 'linear-gradient(180deg, hsl(243 40% 16%) 0%, hsl(260 35% 12%) 100%)';
    
    return (
    <Sidebar
      variant="sidebar"
      side={isRTL ? "right" : "left"}
      className={`border-${isRTL ? 'l' : 'r'} border-white/10 ${isRTL ? 'rtl' : 'ltr'}`}
      collapsible="offcanvas"
      style={{
        background: sidebarBg,
      }}
    >
      <SidebarHeader className="flex items-center justify-center p-2 border-b border-white/10" style={{ background: sidebarBg }}>
        <div className="flex items-center gap-2">
          <img src={logo} alt="دليل يدل - Yadel" className="h-10 w-auto object-contain" />
        </div>
      </SidebarHeader>
      <SidebarContent className="px-1 py-1" style={{ background: sidebarBg }}>
        {menuItems.map((category, idx) => (
          <SidebarGroup key={idx} className="mb-1">
            <SidebarGroupLabel className="px-2 py-1 text-[10px] font-semibold text-white/40 uppercase tracking-wider">
              {category.category[i18n.language as keyof typeof category.category]}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {category.items.map((item, itemIdx) => {
                  const IconComponent = getIcon(item.icon);
                  const badge = getBadge(item.path, item.badge);
                  const isActive = window.location.pathname === item.path;
                  return (
                    <SidebarMenuItem key={itemIdx}>
                      <SidebarMenuButton 
                        tooltip={item.title[i18n.language as keyof typeof item.title]} 
                        onClick={() => navigate(item.path)}
                        className={`px-2 py-1 text-xs rounded-md transition-all duration-200 ${
                          isActive 
                            ? "bg-primary/20 text-white font-medium shadow-sm shadow-primary/20" 
                            : "text-white/70 hover:bg-white/10 hover:text-white"
                        }`}
                        size="sm"
                      >
                        <IconComponent className={`h-3.5 w-3.5 mr-1.5 ${isActive ? 'text-primary-foreground' : 'text-primary/70'}`} />
                        <span>{item.title[i18n.language as keyof typeof item.title]}</span>
                        {badge && (
                          <SidebarMenuBadge className="bg-primary/30 text-primary-foreground text-[10px]">
                            {badge}
                          </SidebarMenuBadge>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      
      <SidebarFooter className="border-t border-white/10 py-1.5 px-2" style={{ background: sidebarBg }}>
        <div className="flex flex-col space-y-1">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-white/70 bg-transparent border border-white/15 hover:bg-white/10 hover:text-white h-7 text-xs"
            onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
          >
            {isRTL ? "English" : "العربية"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-white/70 bg-transparent border border-white/15 hover:bg-white/10 hover:text-white h-7 text-xs"
            onClick={() => navigate("/")}
          >
            <Home className="h-3.5 w-3.5 mr-1.5" />
            {isRTL ? "العودة للرئيسية" : "Back to Home"}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="w-full justify-start bg-destructive/20 text-destructive-foreground hover:bg-destructive/30 border-0 h-7 text-xs"
            onClick={handleSignOut}
          >
            <LogOut className="h-3.5 w-3.5 mr-1.5" />
            {isRTL ? "تسجيل الخروج" : "Sign Out"}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AdminSidebar;
